import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, PhoneCall, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useSimulationForm } from '@/hooks/useSimulationForm';
import FileUploadField from '@/components/simulation/FileUploadField';
import DifficultySelector from '@/components/simulation/DifficultySelector';
import CustomSelect from '@/components/ui/CustomSelect';
import ConfirmDestructiveModal from '@/components/shared/ConfirmDestructiveModal';
import EmailVerificationBanner from '@/components/shared/EmailVerificationBanner.tsx';
import FlowSteps from '@/components/shared/FlowSteps.tsx';
import { maestroIdForDifficulty } from '@/types/maestro';

// Flujo A — "Evalúa tu simulación de venta" (MVP).
// Configurar → (briefing en /simulation/:id) → simulación → informe.
export default function EvaluateFlowPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    form,
    submitting,
    error,
    existingDocs,
    pendingFiles,
    uploadingFiles,
    dragOver,
    rejectedUploads,
    limitReached,
    hasRejectedUploads,
    atDocumentLimit,
    dismissRejectedUpload,
    clearLimitReached,
    showLeaveConfirm,
    salePhases,
    sectors,
    priceRanges,
    simulationTypeOptions,
    subSectors,
    watchSector,
    watchDifficulty,
    onSubmit,
    handleDrop,
    handleFileInput,
    removePendingFile,
    removeExistingDoc,
    setShowLeaveConfirm,
    setDragOver,
  } = useSimulationForm({
    onCreated: (sim) => navigate(`/simulation/${sim.id}`),
  });

  const { register, handleSubmit, watch, setValue, formState: { errors } } = form;

  // MVP: sin selector de maestro — el maestro (perfil de comprador) se asigna
  // según la dificultad elegida, así el usuario afronta distintos perfiles.
  useEffect(() => {
    setValue('assignedMaestroId', maestroIdForDifficulty(watchDifficulty));
  }, [setValue, watchDifficulty]);

  const handleBack = () => {
    if (form.formState.isDirty) {
      setShowLeaveConfirm(true);
    } else {
      navigate('/');
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="mx-auto max-w-3xl animate-fade-in">
        {/* Volver: mismo estilo de enlace que login/registro */}
        <button
          type="button"
          onClick={handleBack}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('mvpFlow.backHome')}
        </button>

        <FlowSteps current={1} />

        <div className="mb-6">
          <EmailVerificationBanner />
        </div>

        {/* Cabecera al estilo de la home / login (icono gradiente Agencium) */}
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-2xl text-white"
            style={{ background: 'var(--gradient-agencium)' }}
          >
            <PhoneCall className="size-7" />
          </div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
            {t('mvpFlow.evaluateTitle')}
          </h1>
          <p className="max-w-xl text-sm text-muted-foreground sm:text-base">
            {t('mvpFlow.evaluateSubtitle')}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div role="alert" className="mb-6 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-6 rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
          {/* Documentos primero, con guía de qué subir (meeting 03/06) */}
          <Field
            label={t('configurator.documentsLabel')}
            hint={t('mvpFlow.docsHelp')}
          >
            <FileUploadField
              existingDocs={existingDocs}
              pendingFiles={pendingFiles}
              uploadingFiles={uploadingFiles}
              dragOver={dragOver}
              rejectedUploads={rejectedUploads}
              atLimit={atDocumentLimit}
              limitReached={limitReached}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onFileInput={handleFileInput}
              onRemovePending={removePendingFile}
              onRemoveExisting={removeExistingDoc}
              onDismissRejected={dismissRejectedUpload}
              onDismissLimitReached={clearLimitReached}
            />
          </Field>

          {/* Objeciones justo debajo de los documentos (meeting 03/06) */}
          <Field label={t('configurator.objectionsLabel')} hint={t('mvpFlow.objectionsHelp')}>
            <Textarea
              {...register('objections')}
              placeholder={t('configurator.objectionsPlaceholder')}
              rows={4}
              className="resize-none"
            />
          </Field>

          {/* Nombre */}
          <Field label={t('configurator.nameLabel')} error={errors.name?.message}>
            <Input {...register('name')} placeholder={t('configurator.namePlaceholder')} />
          </Field>

          {/* Tipo + Fase */}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('configurator.simulationTypeLabel')} error={errors.simulationType?.message}>
              <CustomSelect
                value={watch('simulationType') ?? ''}
                onChange={(val) => setValue('simulationType', val)}
                placeholder={t('configurator.selectType')}
                options={[
                  { value: '', label: t('configurator.selectType') },
                  ...simulationTypeOptions.map((o) => ({ value: o.value, label: o.label })),
                ]}
              />
            </Field>
            <Field label={t('configurator.salePhaseLabel')} error={errors.salePhase?.message}>
              <CustomSelect
                value={watch('salePhase') ?? ''}
                onChange={(val) => setValue('salePhase', val)}
                placeholder={t('configurator.selectPhase')}
                options={[
                  { value: '', label: t('configurator.selectPhase') },
                  ...salePhases.map((p) => ({ value: p.value, label: p.label })),
                ]}
              />
            </Field>
          </div>

          {/* Sector + Sub-sector */}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('configurator.sectorLabel')}>
              <CustomSelect
                value={watch('sector') ?? ''}
                onChange={(val) => setValue('sector', val)}
                placeholder={t('configurator.selectSector')}
                options={[
                  { value: '', label: t('configurator.selectSector') },
                  ...sectors.map((s) => ({ value: s.value, label: s.label })),
                ]}
              />
            </Field>
            <Field label={t('configurator.subSectorLabel')}>
              <CustomSelect
                value={watch('subSector') ?? ''}
                onChange={(val) => setValue('subSector', val)}
                disabled={!watchSector}
                placeholder={watchSector ? t('configurator.selectSubSector') : t('configurator.selectSectorFirst')}
                options={[
                  { value: '', label: watchSector ? t('configurator.selectSubSector') : t('configurator.selectSectorFirst') },
                  ...subSectors.map((s) => ({ value: s.value, label: s.label })),
                ]}
              />
            </Field>
          </div>

          {/* Precio + Idioma */}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('configurator.priceRangeLabel')}>
              <CustomSelect
                value={watch('priceRange') ?? ''}
                onChange={(val) => setValue('priceRange', val)}
                placeholder={t('configurator.selectPriceRange')}
                options={[
                  { value: '', label: t('configurator.selectPriceRange') },
                  ...priceRanges.map((r) => ({ value: r.value, label: r.label })),
                ]}
              />
            </Field>
            <Field
              label={t('configurator.languageLabel')}
              error={errors.language?.message}
            >
              <CustomSelect
                value={watch('language') ?? 'es'}
                onChange={(val) => setValue('language', val as 'es' | 'it', { shouldDirty: true })}
                options={[
                  { value: 'es', label: t('configurator.languageEs') },
                  { value: 'it', label: t('configurator.languageIt') },
                ]}
              />
            </Field>
          </div>

          {/* Dificultad */}
          <Field label={t('configurator.difficultyLabel')}>
            <DifficultySelector
              value={watchDifficulty}
              onChange={(level) => setValue('difficultyLevel', level)}
            />
          </Field>
          </div>

          {/* Submit: a ancho completo, como en login/registro */}
          <Tooltip open={hasRejectedUploads && !submitting ? undefined : false}>
            <TooltipTrigger asChild>
              <div className="w-full">
                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting || hasRejectedUploads}
                  className="w-full"
                >
                  {submitting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Rocket className="size-4" />
                  )}
                  {t('mvpFlow.createAndStart')}
                </Button>
              </div>
            </TooltipTrigger>
            {hasRejectedUploads && !submitting && (
              <TooltipContent>{t('configurator.submitBlockedRejected')}</TooltipContent>
            )}
          </Tooltip>
        </form>
      </div>

      {/* Confirmación al salir con cambios sin guardar */}
      <ConfirmDestructiveModal
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={() => navigate('/')}
        title={t('configurator.leaveTitle')}
        description={t('configurator.leaveMessage')}
        confirmLabel={t('configurator.leaveWithoutSaving')}
        cancelLabel={t('configurator.keepEditing')}
      />
    </TooltipProvider>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative focus-within:z-10">
      <label className="mb-1.5 block text-sm font-medium text-foreground">{label}</label>
      {hint && <p className="mb-2 text-xs text-muted-foreground">{hint}</p>}
      {children}
      {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
    </div>
  );
}
